import { Component, EventEmitter, OnInit, Output, ViewChild } from '@angular/core';
import { FormBuilder, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { TranslateModule } from '@ngx-translate/core';
import { DialogComponent, DialogModule, TooltipModule } from '@syncfusion/ej2-angular-popups';
import { AppErrorMessagesComponent } from 'src/app/base/error-messages/app-error-messages.component';
import { ClientAutocompleteComponent } from 'src/app/base/shared/client-autocomplete/client-autocomplete.component';
import { ParkingRangerAssignmentAdministrationService } from '../parking-ranger-assignment-administration.service';
import { ParkingRangerAssignmentEditFormType } from './parking-ranger-assignment-edit-form.type';
import { AutocompleteComponent } from 'src/app/base/shared/autocomplete/autocomplete.component';
import { map, Observable, of } from 'rxjs';
import { ClientDTO } from 'src/app/base/models/models.swagger.generated';
import { ParkingRangerAssignmentEditZonesSelectorComponent } from './parking-ranger-assignment-edit-zones-selector/parking-ranger-assignment-edit-zones-selector.component';
import { CheckBoxModule } from '@syncfusion/ej2-angular-buttons';
import { NumericTextBoxModule } from '@syncfusion/ej2-angular-inputs';
import { IAutocompleteItem } from 'src/app/base/shared/autocomplete/autocomplete-item';

export type DialogMode = 'add' | 'edit' | 'bulkDelete';

@Component({
  selector: 'app-parking-ranger-assignment-edit-dialog',
  templateUrl: './parking-ranger-assignment-edit-dialog.component.html',
  styleUrls: ['./parking-ranger-assignment-edit-dialog.component.scss'],
  imports: [ReactiveFormsModule, DialogModule, TranslateModule, ClientAutocompleteComponent, AppErrorMessagesComponent, AutocompleteComponent, ParkingRangerAssignmentEditZonesSelectorComponent, TooltipModule, CheckBoxModule, NumericTextBoxModule]
})
export class ParkingRangerAssignmentEditDialogComponent implements OnInit {
  @ViewChild('parkingRangerAssignmentDialog') public dialog: DialogComponent;
  @ViewChild('userAutocomplete') public userAutocomplete: AutocompleteComponent;
  @ViewChild(ParkingRangerAssignmentEditZonesSelectorComponent) public zonesSelector: ParkingRangerAssignmentEditZonesSelectorComponent;

  @Output() public refreshData: EventEmitter<void> = new EventEmitter<void>();

  public mode: DialogMode = 'add';
  public editRowData: any = null;

  public get isAdd(): boolean { return this.mode === 'add'; }
  public get isEdit(): boolean { return this.mode === 'edit'; }
  public get isBulkDelete(): boolean { return this.mode === 'bulkDelete'; }
  public get isAddOrEdit(): boolean { return this.mode === 'add' || this.mode === 'edit'; }

  public form: FormGroup<ParkingRangerAssignmentEditFormType> = this.fb.group<ParkingRangerAssignmentEditFormType>({
    userId: this.fb.control<string>(null, Validators.required),
    client: this.fb.control<ClientDTO>(null),
    isRegistrationCreationEnabled: this.fb.control<boolean>(false),
    isSessionCreationEnabled: this.fb.control<boolean>(false),
    isCurrentPositionEnabled: this.fb.control<boolean>({value: false, disabled: true}),
    isPositionTrackingEnabled: this.fb.control<boolean>({value: false, disabled: true}),
    newSessionCreationAfterHours: this.fb.control<number>(24),
    previewConsideredHours: this.fb.control<number>(24)
  });

  public set selectedUser(value: string) {
    this.form.controls.userId.setValue(value);
  }

  public get selectedUser(): string {
    return this.form.controls.userId.value;
  }

  constructor(private assignmentService: ParkingRangerAssignmentAdministrationService,
    private fb: FormBuilder
  ) { }

  ngOnInit() {
    this.form.controls.isSessionCreationEnabled.valueChanges.subscribe((value: boolean) => {
      if (value) {
        this.form.controls.isCurrentPositionEnabled.enable();
        this.form.controls.isPositionTrackingEnabled.enable();
      } else {
        this.form.controls.isCurrentPositionEnabled.setValue(false);
        this.form.controls.isCurrentPositionEnabled.disable();
        this.form.controls.isPositionTrackingEnabled.setValue(false);
        this.form.controls.isPositionTrackingEnabled.disable();
      }
    });
  }

  public openDialog(mode: DialogMode, rowData?: any) {
    this.mode = mode;
    this.editRowData = rowData || null;

    // Reset form and autocomplete to avoid stale values from previous dialog
    this.form.reset({
      userId: null,
      client: null,
      isRegistrationCreationEnabled: false,
      isSessionCreationEnabled: false,
      isCurrentPositionEnabled: false,
      isPositionTrackingEnabled: false,
      newSessionCreationAfterHours: 24,
      previewConsideredHours: 24
    });
    this.form.controls.isCurrentPositionEnabled.disable();
    this.form.controls.isPositionTrackingEnabled.disable();
    if (this.userAutocomplete) {
      this.userAutocomplete.autocomplete?.clear();
    }

    if (this.isAdd) {
      this.form.controls.client.setValidators(Validators.required);
      this.form.controls.client.updateValueAndValidity();
    } else if (this.isBulkDelete) {
      this.form.controls.client.clearValidators();
      this.form.controls.client.updateValueAndValidity();
    }

    if (this.isEdit && rowData) {
      this.form.patchValue({
        userId: rowData.userId,
        isRegistrationCreationEnabled: rowData.isRegistrationCreationEnabled ?? false,
        isSessionCreationEnabled: rowData.isSessionCreationEnabled ?? false,
        isCurrentPositionEnabled: rowData.isCurrentPositionEnabled ?? false,
        isPositionTrackingEnabled: rowData.isPositionTrackingEnabled ?? false,
        newSessionCreationAfterHours: rowData.newSessionCreationAfterHours ?? 24,
        previewConsideredHours: rowData.previewConsideredHours ?? 24
      });
      if (rowData.isSessionCreationEnabled) {
        this.form.controls.isCurrentPositionEnabled.enable();
        this.form.controls.isPositionTrackingEnabled.enable();
      }
    }

    this.dialog.show();
  }

  public closeDialog() {
    this.dialog.hide();
    this.editRowData = null;
    this.form.reset({
      userId: null,
      client: null,
      isRegistrationCreationEnabled: false,
      isSessionCreationEnabled: false,
      isCurrentPositionEnabled: false,
      isPositionTrackingEnabled: false,
      newSessionCreationAfterHours: 24,
      previewConsideredHours: 24
    });
    this.form.controls.client.markAsUntouched();
    this.form.controls.client.markAsPristine();
    this.form.controls.isCurrentPositionEnabled.disable();
    this.form.controls.isPositionTrackingEnabled.disable();
  }

  public onExecute() {
    if (this.isEdit) {
      this.assignmentService.editAssignment(
        this.editRowData.id,
        this.form.controls.isRegistrationCreationEnabled.value,
        this.form.controls.isSessionCreationEnabled.value,
        this.form.controls.isCurrentPositionEnabled.value,
        this.form.controls.isPositionTrackingEnabled.value,
        this.form.controls.newSessionCreationAfterHours.value,
        this.form.controls.previewConsideredHours.value
      ).subscribe(() => {
        this.refreshData.emit();
        this.closeDialog();
      });
      return;
    }

    if (this.form.invalid) {
      return;
    }

    if (this.isAdd) {
      let selectedZoneIds = this.zonesSelector?.getSelectedZoneIds();
      if (selectedZoneIds?.length === 0) {
        selectedZoneIds = null;
      }
      this.assignmentService.createAssignments(
        this.form.controls.userId.value,
        this.form.controls.client.value?.id,
        selectedZoneIds,
        this.form.controls.isRegistrationCreationEnabled.value,
        this.form.controls.isSessionCreationEnabled.value,
        this.form.controls.isCurrentPositionEnabled.value,
        this.form.controls.isPositionTrackingEnabled.value,
        this.form.controls.newSessionCreationAfterHours.value,
        this.form.controls.previewConsideredHours.value
      ).subscribe(() => {
          this.refreshData.emit();
          this.closeDialog();
        }
      );
    } else if (this.isBulkDelete) {
      this.assignmentService.bulkDeleteRecords(this.form.controls.userId.value, this.form.controls.client.value?.id).subscribe(() => {
          this.refreshData.emit();
          this.closeDialog();
        }
      );
    }
  }

  public getParkingRangers(searchText: string): Observable<IAutocompleteItem[]> {
    if (!searchText) {
      return of([]);
    }
    return this.assignmentService.searchParkingRangers(searchText).pipe(
      map(results => {
        return results.map(r => ({
          id: r.id,
          name: r.userName
        } as IAutocompleteItem));
      })
    );
  }
}
